import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GridCellParams, GridColDef, GridRowParams, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Clear } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useGlobals } from 'src/shared/contexts/appContext';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { deleteEventParticipantTicketsList } from 'src/shared/services/eventParticipantTicketService';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { getParticipantTicketsForEvent } from 'src/shared/services/eventService';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { EventParticipantStatuses } from './eventParticipantStatuses';
import { EventParticipantTicket } from '../models/eventParticipantTicket';
import { EventParticipantTicketSearchFilter } from '../models/eventParticipantTicketSearchFilter';
import { EventTicketDetailModal } from './eventTicketDetailModal';

type EventTicketsDataGridProps = {
  searchFilter: EventParticipantTicketSearchFilter;
  gridColumns?: GridColDef[] | undefined;
  eventId: number;
  eventName?: string;
  eventDate?: string;
  eventLocation?: string;
  onOpenParticipant?: (participantId: number) => void;
};

export const EventTicketsDataGrid = (props: EventTicketsDataGridProps) => {
  const { searchFilter, gridColumns = undefined, eventId, eventName, eventDate, eventLocation, onOpenParticipant } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const { setDateStamp } = useGlobals();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const [rows, setRows] = useState<EventParticipantTicket[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'eventTicketNr', sort: 'desc' }]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);

  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<EventParticipantTicket>();

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));
  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    setTimeout(() => {
      scrollIntoView();
    }, 100);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getParticipantTicketsForEvent(eventId, data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter);
      if (isMounted()) {
        updateData('totalRows', res.data.totalItems);
        setRows(res.data.results);
      }
      ConsoleHelper.log(res);
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [eventId, data.page, data.pageSize, sortModel, searchFilter, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'eventTicketNr',
      headerName: t('event:tableHeaders.ticketNr'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams<EventParticipantTicket>) => {
        return <>{params.row.eventTicketNr || ''}</>;
      },
    },
    {
      field: 'ticket.ticketName',
      headerName: t('event:tableHeaders.ticket'),
      sortable: false,
      flex: 2,
      renderCell: (params: GridCellParams<EventParticipantTicket>) => {
        return <>{params.row.ticket?.ticketName || ''}</>;
      },
    },
    {
      field: 'participant',
      headerName: t('event:tableHeaders.participant'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams<EventParticipantTicket>) => {
        return <>{`${params.row.participant?.firstName} ${params.row.participant?.lastName}` || ''}</>;
      },
    },

    {
      field: 'ticket.invoiceArticleName',
      headerName: t('event:tableHeaders.linkedInvoiceArticle'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams<EventParticipantTicket>) => {
        return <>{params.row.ticket?.invoiceArticleName || ''}</>;
      },
    },

    {
      field: 'isRedeemed',
      headerName: t('event:tableHeaders.status'),
      align: 'right',
      headerAlign: 'right',
      sortable: true,
      flex: 1,

      renderCell: (params: GridCellParams<EventParticipantTicket>) => {
        return <EventParticipantStatuses status={undefined} ticketRedeemed={params.row.isRedeemed} chipSize='small' />;
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await deleteEventParticipantTicketsList(selectedRowIds);
      if (isMounted()) {
        toast.success(
          selectedRowIds.length === 1 ? <>{t('event:deleteParticipantTicketSuccess')}</> : <>{t('event:deleteParticipantTicketsSuccess')}</>,
        );
        setSelectionModel([]);
        setSelectedRowIds([]);
        fetchData();
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setShowConfirmDeleteDialog(false);
        setDateStamp(new Date());
      }
    }
  };

  const onRowClick = (params: GridRowParams<EventParticipantTicket>) => {
    if (params.row) {
      setSelectedItem(params.row);
      setShowDetailsModal(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          '& .MuiDataGrid-cell': {
            cursor: 'pointer',
          },
        }}
        ref={scrollRef}
      >
        <FormLoaderWrapper loading={loading}>
          <DataGrid<EventParticipantTicket>
            autoHeight
            columns={gridColumns ?? columns}
            rows={rows}
            disableSelectionOnClick
            checkboxSelection
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            onRowClick={onRowClick}
            sortModel={sortModel}
            selectionModel={selectionModel}
            dataModel={data}
            onSortModelChange={(newSortModel) => {
              if (newSortModel.length) {
                setSortModel(newSortModel);
              } else {
                setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
              }
            }}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </FormLoaderWrapper>
      </Box>
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }}>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button
              variant='contained'
              disableElevation
              color='error'
              sx={{ mr: 1 }}
              onClick={() => {
                const selectedIDs = new Set(selectionModel);
                const selection = rows.filter((row) => selectedIDs.has(row.id || 0)).map((x) => x.id);
                setSelectedRowIds(selection);
                setShowConfirmDeleteDialog(true);
              }}
            >
              {t('common:button.delete')}
            </Button>
          </Box>
        </FixedFooter>
      )}
      {showConfirmDeleteDialog && (
        <DeleteConfirmationDialog
          open={showConfirmDeleteDialog}
          title={t('event:deleteParticipantConfirmTitle')}
          value=''
          modaltext={
            selectedRowIds.length === 1 ? t('event:deleteParticipantTicketConfirmDescription') : t('event:deleteParticipantsTicketConfirmDescription')
          }
          onClose={(value) => {
            if (value) {
              handleDeleteConfirm();
            } else {
              setShowConfirmDeleteDialog(false);
            }
          }}
        />
      )}
      {showDetailsModal && selectedItem && (
        <EventTicketDetailModal
          modalOpen={showDetailsModal}
          ticket={selectedItem}
          eventName={eventName}
          eventDate={eventDate}
          eventLocation={eventLocation}
          onClose={() => setShowDetailsModal(false)}
          refetchData={() => {
            setShowDetailsModal(false);
            fetchData();
          }}
          onOpenParticipant={onOpenParticipant}
        />
      )}
    </>
  );
};
